// src/auth/route-owner.ts
//
// A ledger of which person visited which pages in this browser: `{ v: 2, lastUserId, pages }`, with `pages` a map
// from user id to the last 30 paths (`pathname + search`) that person was on, most recent last, for at most 5 users.
// It is how a sign-in tells "a link" from "a page left open by someone else". A session that ends keeps its URL
// (the login page is drawn over it), so a sign-in by ANOTHER person on a path another person visited would open
// that person's page (an encounter, say): it goes home instead. A path no other person visited is a link and stays.
//   - ProtectedRoute adds the current path for the signed-in person on every route change, and logout adds the
//     page it ends on. A ledger of pages, not one marker, because one person can have several tabs (the last
//     writer would win), and sessionStorage alone is lost when the browser restores its tabs after a restart.
//   - Hydrate adds NO path: the URL a tab opens on is not the page that person was on. It only names the person
//     in `lastUserId` when none is named, so their leftovers are cleared at the next sign-in.
//   - A sign-in compares first and then sets `lastUserId`. Other people's pages stay, so a third person signing in
//     on a second expired tab is also sent home.
//   - The ledger is capped, and another tab can push a page out of it (a tab whose in-memory session outlives this
//     tab's logout can visit 30 pages). So logout also writes this TAB's end page to sessionStorage
//     (`scrips.session_end`), which no other tab can touch. A sign-in on that same path by another person is a
//     route change, and the sign-in removes the marker. Hydrate never writes it: the opened URL is not theirs,
//     and a restored tab keeps its sessionStorage.
// Only user ids and paths are stored, no profile or token. Every read and write is guarded: storage can be missing
// or blocked, and malformed or old-shape JSON reads as an empty ledger and is overwritten by the next write.
const KEY = 'scrips.route_owner';
const TAB_END_KEY = 'scrips.session_end';
export const MAX_PAGES_PER_USER = 30;
export const MAX_USERS = 5;

export interface RouteLedger {
  v: 2;
  lastUserId: string | null;
  /** Keyed by normalized user id; key order is oldest user first. */
  pages: Record<string, string[]>;
}

/** Identity ids are GUIDs; compare them trimmed and case-insensitively (same rule as auth-store `sameIdentity`). */
const norm = (id: string | null | undefined): string => (id ?? '').trim().toLowerCase();

function emptyLedger(): RouteLedger {
  return { v: 2, lastUserId: null, pages: Object.create(null) as Record<string, string[]> };
}

function read(): RouteLedger {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return emptyLedger();
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v === null) return emptyLedger();
    const { v: version, lastUserId, pages } = v as Record<string, unknown>;
    if (version !== 2 || typeof pages !== 'object' || pages === null) return emptyLedger();
    const ledger = emptyLedger();
    ledger.lastUserId = typeof lastUserId === 'string' && norm(lastUserId) !== '' ? norm(lastUserId) : null;
    for (const [id, list] of Object.entries(pages as Record<string, unknown>)) {
      if (norm(id) === '' || !Array.isArray(list)) continue;
      ledger.pages[norm(id)] = list.filter((p): p is string => typeof p === 'string').slice(-MAX_PAGES_PER_USER);
    }
    return ledger;
  } catch {
    return emptyLedger();
  }
}

function write(ledger: RouteLedger): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(ledger));
  } catch {
    // Storage blocked: nothing is remembered, and every sign-in keeps its URL.
  }
}

function addPageTo(ledger: RouteLedger, userId: string, path: string): void {
  const id = norm(userId);
  const kept = (ledger.pages[id] ?? []).filter((p) => p !== path);
  kept.push(path); // most recent last
  delete ledger.pages[id]; // re-insert, so this person is the newest user
  ledger.pages[id] = kept.slice(-MAX_PAGES_PER_USER);
  const ids = Object.keys(ledger.pages);
  for (const old of ids.slice(0, Math.max(0, ids.length - MAX_USERS))) delete ledger.pages[old];
  ledger.lastUserId = id;
}

/** Read and remove this tab's end marker. Malformed or blocked reads as none. */
function takeTabEnd(): { userId: string; path: string } | null {
  try {
    const raw = sessionStorage.getItem(TAB_END_KEY);
    sessionStorage.removeItem(TAB_END_KEY);
    if (!raw) return null;
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v === null) return null;
    const { userId, path } = v as Record<string, unknown>;
    return typeof userId === 'string' && norm(userId) !== '' && typeof path === 'string' ? { userId, path } : null;
  } catch {
    return null;
  }
}

export const routeOwnerStorage = {
  /** This tab's session ends on `path` (logout): no other tab can evict it, unlike a ledger entry. */
  setTabEnd: (userId: string, path: string): void => {
    if (norm(userId) === '') return;
    try {
      sessionStorage.setItem(TAB_END_KEY, JSON.stringify({ userId, path }));
    } catch {
      // Storage blocked: the ledger still holds the page.
    }
  },
  /** The signed-in person is on `path` (route change), or their session ends on it (logout). */
  addPage: (userId: string, path: string): void => {
    if (norm(userId) === '') return;
    const ledger = read();
    addPageTo(ledger, userId, path);
    write(ledger);
  },
  /** Hydrate found a stored profile it could not keep: name that person if none is named. No page is added. */
  noteStaleUser: (userId: string): void => {
    if (norm(userId) === '') return;
    const ledger = read();
    if (ledger.lastUserId === null) ledger.lastUserId = norm(userId);
    write(ledger);
  },
  /**
   * A sign-in by `userId` on `path`. `routeOwnerChanged`: another person visited this path (ledger), or this tab's
   * session of another person ended on it (tab marker, removed here). `sessionOwnerChanged`:
   * another person was the last one here. Then `userId` becomes the last person. Pages are left as they are.
   */
  signIn: (userId: string, path: string): { routeOwnerChanged: boolean; sessionOwnerChanged: boolean } => {
    const me = norm(userId);
    const ledger = read();
    const tabEnd = takeTabEnd();
    const routeOwnerChanged =
      Object.entries(ledger.pages).some(([id, list]) => id !== me && list.includes(path)) ||
      (tabEnd !== null && norm(tabEnd.userId) !== me && tabEnd.path === path);
    const sessionOwnerChanged = ledger.lastUserId !== null && ledger.lastUserId !== me;
    ledger.lastUserId = me;
    write(ledger);
    return { routeOwnerChanged, sessionOwnerChanged };
  },
  /** The whole ledger, for tests and diagnostics. */
  read,
};

/** The path the ledger records: where the browser is now. */
export function currentPath(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname + window.location.search;
}
